import React from "react";
import { twMerge } from "tailwind-merge";
import PillarTableBody from "shared/components/pillar-table/body/PillarTableBody";
import AdminReportInformation from "shared/components/pillar-table/custom-reports/AdminReportInformation";
import PillarTableHeader from "shared/components/pillar-table/header/PillarTableHeader";
import { usePillarTableDynamicHeight } from "shared/components/pillar-table/hooks/usePillarTableDynamicHeight";
import { assertNoPillarTableRowClickWithNestedTable } from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import PillarTablePagination from "shared/components/pillar-table/pagination-sorting/PillarTablePagination";
import {
  usePillarTableContext,
  PaginationType,
} from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { base_table } from "shared/prisma/base-table";

const PillarTableMain = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>() => {
  const {
    emptyTableMessage,
    loadedFirstTime,
    wrapperClassName,
    customReportResponse,
    tableRowResults,
    dataQuery,
    dataQueryError,
    dynamicFilterChoices,
    totalResults,
    extraProps,
    columns,
    setFilters,
    refetchData,
    HeaderBarComponentWrapper,
    FilterComponentWrapper,
    className,
    testidTable,
    pagination,
    paginationState,
    setPaginationState,
    toggleSorting,
    fixedColumnWidths,
    handleRowClick,
    handleInlineEdit,
    handleInlineDelete,
    columnRefs,
    filtersRequest,
    queryKey,
    urlParams,
    isFetchingPreviousPage,
    isFetchingNextPage,
    tableRef,
    virtualRows,
    totalTableHeight,
    isInfinityTable,
    isTableWithFixedHeight,
    renderCustomReportColumn,
    isDynamicHeight,
    tablePaginationRef,
    ...props
  } = usePillarTableContext<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >();

  // A misconfiguration no runtime behaviour can resolve, so it fails here
  // rather than silently letting the row click win over the nested table.
  assertNoPillarTableRowClickWithNestedTable({
    testidTable,
    handleRowClick,
    columns,
  });

  const {
    headerAboveTableRef,
    tableScrollWrapperStyle,
    tableScrollWrapperClasses,
  } = usePillarTableDynamicHeight();

  return (
    <div
      className={`flex flex-col justify-start items-start w-full ${
        wrapperClassName ?? ""
      }`}
      {...props}
    >
      <div ref={headerAboveTableRef} className="w-full">
        <AdminReportInformation />
        {HeaderBarComponentWrapper && <HeaderBarComponentWrapper />}
        {FilterComponentWrapper && <FilterComponentWrapper />}
      </div>
      <div
        ref={isInfinityTable ? tableRef : undefined}
        className={twMerge(
          isDynamicHeight && "table-scrollbar rounded-t-xl",
          isDynamicHeight ? "" : "!rounded-tl-lg !rounded-tr-lg",

          tableScrollWrapperClasses,
          className ?? "",
          isInfinityTable &&
            isTableWithFixedHeight &&
            "overflow-auto h-[600px]",
        )}
        style={tableScrollWrapperStyle}
      >
        <table
          data-testid={testidTable ? `${testidTable}-table` : undefined}
          className={` ${
            pagination === true || pagination === PaginationType.Regular
              ? "pillartable-table-base"
              : "pillartable-table-base-roundbottom"
          }
           w-full 
           ${isDynamicHeight ? "!rounded-tl-none !rounded-tr-none" : "!rounded-tl-lg !rounded-tr-lg"}`}
        >
          <PillarTableHeader />
          <PillarTableBody<RowGeneric> />
        </table>
      </div>
      {(pagination === true || pagination === PaginationType.Regular) &&
        tableRowResults &&
        dataQuery instanceof Function &&
        customReportResponse?.baseTable !== base_table.raw_query && (
          <PillarTablePagination
            testid={testidTable}
            results={totalResults}
            paginationState={paginationState}
            handler={(newPagination) =>
              setPaginationState({ ...paginationState, ...newPagination })
            }
          />
        )}
    </div>
  );
};
export default PillarTableMain;
